import { useEffect, useRef, useState } from "react";
import { Award, Eye, Handshake, Gem } from "lucide-react";

const BenefitsSection = ({ category, config }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Default benefits si no hay config
  const defaultBenefits = [
    {
      title: "Resultados Comprobados",
      description: "Miles de trámites exitosos nos respaldan con una tasa de éxito del 95%",
      image: "/images/benefit-1.jpg"
    },
    {
      title: "Transparencia Total",
      description: "Seguimiento en tiempo real de cada etapa de tu trámite las 24 horas",
      image: "/images/benefit-2.jpg"
    },
    {
      title: "Atención Personalizada",
      description: "Un gestor dedicado te acompaña durante todo el proceso",
      image: "/images/benefit-3.jpg"
    },
    {
      title: "Calidad Premium",
      description: "Servicio de primera clase con garantía de satisfacción total",
      image: "/images/benefit-4.jpg"
    }
  ];

  const benefits = (config && config.benefits && config.benefits.length > 0) 
    ? config.benefits 
    : defaultBenefits;

  return (
    <section ref={sectionRef} className="py-20 bg-white relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-100 to-purple-100 rounded-full opacity-30 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-pink-100 to-orange-100 rounded-full opacity-30 blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
        }`}>
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-100 to-purple-100 px-4 py-2 rounded-full mb-4">
            <span className="text-blue-600 font-semibold text-sm">NUESTROS BENEFICIOS</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            ¿Por Qué Elegir{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Nuestros Servicios?
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Somos líderes en gestión de trámites con resultados comprobados y miles de clientes satisfechos
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {benefits.map((benefit, index) => (
            <BenefitCard
              key={index}
              benefit={benefit}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const BenefitCard = ({ benefit, index, isVisible }) => {
  const icons = [Award, Eye, Handshake, Gem];
  const gradients = [
    'from-blue-500 to-cyan-500',
    'from-purple-500 to-pink-500',
    'from-orange-500 to-red-500',
    'from-green-500 to-emerald-500'
  ];

  const Icon = benefit.icon || icons[index % icons.length];
  const gradient = gradients[index % gradients.length];
  const delay = `${index * 100}ms`;

  return (
    <div
      className={`group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
      }`}
      style={{ transitionDelay: delay }}
    >
      {/* Image at top */}
      {benefit.image && (
        <div className="relative h-48 overflow-hidden">
          <img
            src={benefit.image}
            alt={benefit.title}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
          />
          <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-40 group-hover:opacity-30 transition-opacity duration-500`}></div>
          
          {/* Floating icon */}
          <div className={`absolute top-4 right-4 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-500`}>
            <Icon className={`w-6 h-6 bg-gradient-to-br ${gradient} bg-clip-text text-transparent`} style={{ WebkitTextFillColor: 'transparent' }} />
          </div>
        </div>
      )}

      <div className="p-6">
        {/* Icon if no image */}
        {!benefit.image && (
          <div className={`w-14 h-14 bg-gradient-to-br ${gradient} rounded-2xl flex items-center justify-center mb-4 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
            <Icon className="w-7 h-7 text-white" />
          </div>
        )}

        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300">
          {benefit.title}
        </h3>
        <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
      </div>

      {/* Decorative gradient line at bottom */}
      <div className={`h-1 bg-gradient-to-r ${gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
    </div>
  );
};

export default BenefitsSection;